import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ReferenceKind, ReferenceUpload, ReferenceUploadsResponse } from '../types/referenceData';
import {
  createReferenceUpload, deleteReferenceUpload, listReferenceUploads,
  referenceErrorText, sendReferenceFile, sha256File,
} from '../utils/referenceDataApi';
import Button from './ui/Button';
import { Input } from './pilot-ui/input';
import { Label } from './pilot-ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './pilot-ui/select';

interface Props { deviceName: string }
const POLL_INTERVAL_MS = 2000;
const POLL_LIMIT_MS = 120000;
const isFinishedUpload = (upload: ReferenceUpload | undefined): upload is ReferenceUpload =>
  upload?.status === 'loaded' || upload?.status === 'rejected' || upload?.status === 'failed';

const UploadResult: React.FC<{ upload: ReferenceUpload }> = ({ upload }) => (
  <div role="status" className="text-sm">
    {upload.status === 'rejected' || upload.status === 'failed' ? (
      <p>{upload.message}</p>
    ) : (
      <>
        <p>Loaded {upload.rows} rows, {upload.first_t} to {upload.last_t}.</p>
        {upload.warnings.map(warning => <p key={warning}>Warning: {warning}</p>)}
      </>
    )}
  </div>
);

const ReferenceUploadsPanel: React.FC<Props> = ({ deviceName }) => {
  const [listing, setListing] = useState<ReferenceUploadsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [kind, setKind] = useState<ReferenceKind>('thermostat');
  const [timeZone, setTimeZone] = useState('');
  const [label, setLabel] = useState('');
  const [sending, setSending] = useState(false);
  const [activeUploadId, setActiveUploadId] = useState('');
  const [pollStartedAt, setPollStartedAt] = useState(0);
  const [result, setResult] = useState<ReferenceUpload | null>(null);
  const [timedOut, setTimedOut] = useState(false);
  const polling = useRef(false);

  const refresh = useCallback(async (): Promise<ReferenceUploadsResponse> => {
    const next = await listReferenceUploads(deviceName);
    setListing(next);
    setTimeZone(current => current || next.default_time_zone);
    return next;
  }, [deviceName]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setListing(null);
    setError('');
    listReferenceUploads(deviceName).then(next => {
      if (!active) return;
      setListing(next);
      setTimeZone(next.default_time_zone);
    }).catch(err => {
      if (active) setError(referenceErrorText(err));
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [deviceName]);

  useEffect(() => {
    if (!activeUploadId || timedOut) return;
    let active = true;
    const timer = window.setInterval(async () => {
      if (Date.now() - pollStartedAt >= POLL_LIMIT_MS) {
        setTimedOut(true);
        return;
      }
      if (polling.current) return;
      polling.current = true;
      try {
        const next = await listReferenceUploads(deviceName);
        if (!active) return;
        setListing(next);
        setError('');
        const upload = next.uploads.find(item => item.upload_id === activeUploadId);
        if (isFinishedUpload(upload)) {
          setResult(upload);
          setActiveUploadId('');
        }
      } catch (err) {
        if (active) setError(referenceErrorText(err));
      } finally {
        polling.current = false;
      }
    }, POLL_INTERVAL_MS);
    return () => { active = false; window.clearInterval(timer); };
  }, [activeUploadId, deviceName, pollStartedAt, timedOut]);

  const handleRefresh = async () => {
    setError('');
    try {
      const next = await refresh();
      const upload = next.uploads.find(item => item.upload_id === activeUploadId);
      if (isFinishedUpload(upload)) {
        setResult(upload);
        setActiveUploadId('');
        setTimedOut(false);
      }
    } catch (err) {
      setError(referenceErrorText(err));
    }
  };

  const handleUpload = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!file) return;
    setError('');
    setResult(null);
    setTimedOut(false);
    setSending(true);
    try {
      const sha256 = await sha256File(file);
      const created = await createReferenceUpload(deviceName, {
        kind,
        file_name: file.name,
        size_bytes: file.size,
        sha256,
        ...(kind === 'air_monitor' ? {} : { time_zone: timeZone }),
        ...(label.trim() ? { label: label.trim() } : {}),
      });
      await sendReferenceFile(created.post, file);
      setActiveUploadId(created.upload.upload_id);
      setPollStartedAt(Date.now());
      await refresh();
    } catch (err) {
      setError(referenceErrorText(err));
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (uploadId: string) => {
    setError('');
    try {
      await deleteReferenceUpload(deviceName, uploadId);
      await refresh();
    } catch (err) {
      setError(referenceErrorText(err));
    }
  };

  const kindChoice = listing?.kinds.find(choice => choice.kind === kind);
  return (
    <section aria-labelledby="reference-uploads-heading" className="mt-8 border-t border-border pt-6">
      <h2 id="reference-uploads-heading" className="text-xl font-semibold mb-4">Reference data</h2>
      {loading && <p role="status">Loading reference data…</p>}
      {error && <p role="alert" className="text-status-error-foreground">{error}{!listing && <Button variant="link" title="Retry loading outside upload history" onClick={handleRefresh}>Retry</Button>}</p>}
      {listing && (
        <>
          <h3 className="font-semibold">Current sources</h3>
          {listing.sources.length === 0 ? <p className="text-sm text-muted-foreground">No outside readings uploaded.</p> : (
            <ul className="list-disc pl-5 text-sm">
              {listing.sources.map(source => <li key={source.kind}>{source.label} ({source.kind.replace('_', ' ')}): {source.first_day} to {source.last_day}; {source.days_with_data} days with data</li>)}
            </ul>
          )}

          <h3 className="font-semibold mt-6 mb-2">Upload reference data</h3>
          <form onSubmit={handleUpload} className="space-y-4">
            <div>
              <Label htmlFor="reference-file" className="mb-1">File</Label>
              <Input id="reference-file" type="file" accept=".csv,.xlsx" required title="Choose a CSV or Excel file of outside readings for this Clip" aria-describedby="reference-file-help" onChange={event => setFile(event.target.files?.[0] ?? null)} />
              <p id="reference-file-help" className="text-xs text-muted-foreground">CSV or Excel outside readings, up to {Math.round(listing.limits.max_bytes / 1048576)} MB.</p>
            </div>
            <div>
              <Label htmlFor="reference-kind" className="mb-1">Source kind</Label>
              <Select value={kind} items={listing.kinds.map(choice => ({ value: choice.kind, label: choice.label }))} onValueChange={value => { if (value) setKind(value); }}>
                <SelectTrigger id="reference-kind" title="Choose which outside equipment produced these readings" aria-describedby="reference-kind-help" className="w-full"><SelectValue /></SelectTrigger>
                <SelectContent listLabel="Reference source kinds">
                  {listing.kinds.map(choice => <SelectItem key={choice.kind} value={choice.kind}>{choice.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <p id="reference-kind-help" className="text-xs text-muted-foreground">{kindChoice?.formats}</p>
            </div>
            {kind !== 'air_monitor' && (
              <div>
                <Label htmlFor="reference-time-zone" className="mb-1">File time zone</Label>
                <Select value={timeZone} items={listing.time_zones.map(zone => ({ value: zone, label: zone }))} onValueChange={value => setTimeZone(value ?? '')}>
                  <SelectTrigger id="reference-time-zone" title="Choose the local time zone used to interpret the file's timestamps" aria-describedby="reference-time-zone-help" className="w-full"><SelectValue /></SelectTrigger>
                  <SelectContent listLabel="Reference time zones">
                    {listing.time_zones.map(zone => <SelectItem key={zone} value={zone}>{zone}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p id="reference-time-zone-help" className="text-xs text-muted-foreground">The local time zone of the uploaded readings.</p>
              </div>
            )}
            <div>
              <Label htmlFor="reference-label" className="mb-1">Label (optional)</Label>
              <Input id="reference-label" type="text" maxLength={60} value={label} title="Name this outside source in charts and upload history" aria-describedby="reference-label-help" onChange={event => setLabel(event.target.value)} />
              <p id="reference-label-help" className="text-xs text-muted-foreground">Shown beside this source's charts.</p>
            </div>
            <Button type="submit" variant="primary" disabled={sending || !file} title="Upload outside readings for this Clip">{sending ? 'Sending…' : 'Upload'}</Button>
          </form>

          {activeUploadId && !timedOut && <p role="status" className="mt-3">File sent. Reading outside data…</p>}
          {timedOut && <div role="status" className="mt-3">The file was sent but is still being read. <Button variant="secondary" title="Refresh the upload status" onClick={handleRefresh}>Refresh</Button></div>}
          {result && <UploadResult upload={result} />}

          <h3 className="font-semibold mt-6 mb-2">Uploads</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr>
                <th scope="col">File</th><th scope="col">Kind</th><th scope="col">Status</th>
                <th scope="col">Dates covered</th><th scope="col">Rows</th><th scope="col">Uploaded by</th>
                <th scope="col">When</th><th scope="col">Message or warnings</th><th scope="col">Action</th>
              </tr></thead>
              <tbody>
                {listing.uploads.map(upload => <tr key={upload.upload_id} className="border-t border-border">
                  <td>{upload.file_name}</td><td>{upload.kind.replace('_', ' ')}</td><td>{upload.status}</td>
                  <td>{upload.first_t && upload.last_t ? `${upload.first_t} to ${upload.last_t}` : '—'}</td>
                  <td>{upload.rows}</td><td>{upload.created_by}</td><td>{upload.created_at}</td>
                  <td>{[upload.message, ...upload.warnings].filter(Boolean).join('; ')}{upload.status === 'deleted' && <span> Deleted by {upload.deleted_by} at {upload.deleted_at}.</span>}</td>
                  <td>{upload.status !== 'deleted' && <Button variant="danger" title={`Delete ${upload.file_name} and its outside readings`} onClick={() => handleDelete(upload.upload_id)}>Delete</Button>}</td>
                </tr>)}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
};

export default ReferenceUploadsPanel;
